import countriesConfig from "@/countries.config.json" with { type: "json" };
import type { NewsletterForm } from "@/types/newsletter";
import { buildPreferences, buildSubscriptions } from "@/utils/cdc.utils";
import type { Brand, CountryConfig } from "./middleware.utils";

export interface PhoneNumberData {
  type: string;
  number: string;
  default: boolean;
}

export function buildBodyDataForNewsletter(validatedFormData: NewsletterForm) {
  const bodyData = new URLSearchParams();

  const profile = buildProfileForNewsletter(validatedFormData);
  bodyData.append("profile", JSON.stringify(profile));

  const data = buildDataForNewsletter(validatedFormData);
  bodyData.append("data", JSON.stringify(data));

  const preferences = buildPreferences(validatedFormData.joinRidersClub);
  bodyData.append("preferences", JSON.stringify(preferences));

  const interestFormat: string[] = [];
  (validatedFormData.interest || []).forEach((i: string) => {
    const key = mapInterestNameToSubscription[i];
    if (key) {
      interestFormat.push(key);
    }
  });
  const subscriptions = buildSubscriptions(
    interestFormat,
    validatedFormData.country
  );
  bodyData.append("subscriptions", JSON.stringify(subscriptions));

  return bodyData;
}

function buildDataForNewsletter(validatedFormData: NewsletterForm) {
  const data: {
    ietf_language_tag: string;
  } = {
    ietf_language_tag: validatedFormData.ietf_language_tag,
  };

  return data;
}

function buildProfileForNewsletter(validatedFormData: NewsletterForm) {
  const profile: {
    email: string;
    country: string;
    locale: string;
    firstName?: string;
    lastName?: string;
    phones?: PhoneNumberData;
  } = {
    email: validatedFormData.email,
    country: validatedFormData.country,
    locale: validatedFormData.preferredLanguage,
  };

  if (validatedFormData.firstName) {
    profile.firstName = validatedFormData.firstName;
  }

  if (validatedFormData.lastName) {
    profile.lastName = validatedFormData.lastName;
  }

  if (validatedFormData.phoneNumber) {
    profile.phones = buildPhoneData(validatedFormData.phoneNumber);
  }

  return profile;
}

export function buildPhoneData(phoneNumber: string): PhoneNumberData {
  const phoneData: PhoneNumberData = {
    number: phoneNumber,
    type: "",
    default: true,
  };
  return phoneData;
}

export function computedDatas(
  formData: NewsletterForm,
  languageParam: string,
  countryParam: string,
  isLanguageSelectionVisible: boolean,
  principalLanguages: string[],
  brand: string
) {
  const computedIetfLanguageTag = isLanguageSelectionVisible
    ? formData.preferredLanguage
    : `${languageParam}-${countryParam.toUpperCase()}`;

  const computedPreferredLanguage = principalLanguages.includes(
    formData.preferredLanguage.toLowerCase()
  )
    ? formData.preferredLanguage
    : "en";

  const verifiedCountryParam =
    (countriesConfig as CountryConfig)[brand as Brand][
      countryParam.toLowerCase()
    ]?.isEcommerce === false
      ? ""
      : countryParam.toUpperCase();

  const finalData = {
    ...formData,
    preferredLanguage: computedPreferredLanguage,
    ietf_language_tag: computedIetfLanguageTag,
    country: verifiedCountryParam,
  };
  return finalData;
}

export const mapInterestNameToSubscription: Record<string, string> = {
  bike: "Scott_bike",
  running: "Scott_running",
  travel: "Scott_travel",
  motosports: "Scott_motosports",
  wintersports: "Scott_wintersports",
};

export const mapSubCategoryToAPI: Record<string, string> = {
  mountainBiking: "Scott_bikeMountainBiking",
  roadCycling: "Scott_bikeRoadCycling",
  gravelCycling: "Scott_bikeGravelCycling",
  cityUrban: "Scott_bikeCityUrban",
  triathlon: "Scott_bikeTriathlon",
  trailRunning: "Scott_runningTrail",
  roadRunning: "Scott_runningRoad",
  freeride: "Scott_wintersportsFreeride",
  skiTouring: "Scott_wintersportsSkiTouring",
  allMountain: "Scott_wintersportsAllMountain",
  adventure: "Scott_motosportsAdventure",
  offRoad: "Scott_motosportsOffRoad",
  snowmobile: "Scott_motosportsSnowmobile",
};

export const newsletterPreferences = [
  {
    name: "bike",
    value: [
      { id: 1, value: "mountainBiking" },
      { id: 2, value: "roadCycling" },
      { id: 3, value: "gravelCycling" },
      { id: 4, value: "cityUrban" },
      { id: 5, value: "triathlon" },
    ],
  },
  {
    name: "running",
    value: [
      { id: 1, value: "trailRunning" },
      { id: 2, value: "roadRunning" },
    ],
  },
  {
    name: "wintersports",
    value: [
      { id: 1, value: "freeride" },
      { id: 2, value: "skiTouring" },
      { id: 3, value: "allMountain" },
    ],
  },
  {
    name: "motosports",
    value: [
      { id: 1, value: "adventure" },
      { id: 2, value: "offRoad" },
      { id: 3, value: "snowmobile" },
    ],
  },
  {
    name: "travel",
    value: [],
  },
];

export const InterestsByBrand: Record<string, string[]> = {
  scott: ["bike", "running", "wintersports", "motosports", "travel"],
  dolomite: [],
  syncros: [],
  bergamont: [],
};

export const USInterests: string[] = [
  "bike",
  "running",
  "wintersports",
  "motosports",
];
